import { ReactElement, useCallback, useEffect } from 'react';
import { useForm, WatchObserver } from 'react-hook-form';
import {
  Checkbox,
  StatusBar,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Typography,
  SnackbarStatus,
} from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { ProductRangeSelector } from '../ProductRangeSelector/ProductRangeSelector';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { updateMenuRanges, IConfigureUpdateMenuRangesRequest } from '../../api/updateMenuRanges/updateMenuRanges';
import {
  configureRangesText,
  mustSelectRangeErrorMessage,
  NEW_RANGE_CONFIGURATIONS,
  newRangeConfigurationsCheckboxText,
} from './constants';
import styles from './EditMenuRangesModal.module.scss';
import { IFormData } from '../MenuModal/types';
import menuImage from 'src/assets/images/create-menu-modal.png';
import { EntitySource } from 'src/constants';
import { Status } from 'src/api/constants';
import { Range } from '@weareopenr/types';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';

interface IEditMenuRangesModalProps {
  closeModal: () => void;
  menuId: string;
  currentRanges: Range[];
}

const Content = ({ closeModal, menuId, currentRanges }: IEditMenuRangesModalProps): ReactElement => {
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: ranges } = useRanges();
  const queryClient = useQueryClient();
  const menuRangeIds = currentRanges.map((range) => range.range_id);

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    control,
  } = useForm<IFormData>({
    defaultValues: {
      ranges: menuRangeIds,
      source: currentRanges.length > 0 ? EntitySource.RANGES : EntitySource.LIBRARY,
      newRangeConfigurations: false,
    },
  });

  const watchedCheckbox = watch('newRangeConfigurations');

  const clearRangeErrors = useCallback<WatchObserver<IFormData>>(
    (value, { name, type }) => {
      if (name === 'source' && type === 'change' && value.source === EntitySource.LIBRARY) {
        clearErrors('ranges');
      }
    },
    [clearErrors],
  );

  useEffect(() => {
    const subscription = watch(clearRangeErrors);
    return () => subscription.unsubscribe();
  }, [watch, clearRangeErrors]);

  const onSubmit = async (data: IFormData) => {
    clearError();
    if (data.source === EntitySource.RANGES && (!data.ranges || data.ranges.length === 0)) {
      setError('ranges', { message: mustSelectRangeErrorMessage });
      return;
    }

    const payloadConfigureRanges: IConfigureUpdateMenuRangesRequest = {
      ranges: data.source === 'ranges' ? data.ranges : [],
    };

    try {
      const res = await updateMenuRanges(menuId, payloadConfigureRanges);

      if (res.status !== Status.SUCCESS) {
        const json = await res.json();
        throw new Error(json);
      }

      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });

      showNotification({
        status: SnackbarStatus.SUCCESS,
        message: 'Product ranges successfully updated',
      });

      closeModal();
    } catch (e) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request. Please try again',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['title-ranges']}>
        Configure Ranges
      </Typography>

      <Typography size="medium" weight="regular" className={styles['ranges-info-text']}>
        {configureRangesText}
      </Typography>

      <ProductRangeSelector
        ranges={ranges || currentRanges}
        register={register}
        clearErrors={clearErrors}
        errors={errors}
        setValue={setValue}
        watch={watch}
        productType="BUNDLE"
        control={control}
      />
      <div className={styles['checkbox-container']}>
        <Checkbox
          name={NEW_RANGE_CONFIGURATIONS}
          register={register}
          label={newRangeConfigurationsCheckboxText}
          disabled={isSubmitting}
          labelWeight="regular"
        />

        <ButtonContainer
          left={
            <SecondaryButton
              data-cy="create-button"
              buttonStyle="outline"
              size="full-width"
              type="button"
              onClick={closeModal}
            >
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              data-cy="create-button"
              loading={isSubmitting}
              size="full-width"
              colour="blueberry"
              disabled={!watchedCheckbox || !!errors.ranges}
              type="submit"
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export const EditMenuRangesModal = ({
  menuId,
  isOpen,
  closeModal,
  currentRanges,
}: IEditMenuRangesModalProps & {
  isOpen: boolean;
}): ReactElement => {
  return (
    <Modal open={isOpen} closeModal={closeModal} image={menuImage} imageAlt="Picture of menus">
      <Content menuId={menuId} closeModal={closeModal} currentRanges={currentRanges} />
    </Modal>
  );
};

export default EditMenuRangesModal;
